import { format } from "date-fns";
import { Command, Menu, Moon, Search, Sun } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useRouterState } from "@tanstack/react-router";
import { useAuth } from "@/hooks/useAuth";
import { useDarkMode } from "@/hooks/useDarkMode";
import { usePeriodFilterContext } from "@/hooks/usePeriodFilterContext";
import { supabase } from "@/lib/supabase";
import { getRouteTitle } from "@/lib/navigation";
import { openCommandPalette } from "@/components/layout/CommandPalette";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { getUserDisplayName } from "@/lib/auth";

const periodOptions = [
  { value: "today", label: "Hoje" },
  { value: "yesterday", label: "Ontem" },
  { value: "7d", label: "7 dias" },
  { value: "14d", label: "14 dias" },
  { value: "30d", label: "30 dias" },
  { value: "90d", label: "90 dias" },
  { value: "6m", label: "6 meses" },
  { value: "1y", label: "1 ano" },
  { value: "all", label: "Todo periodo" },
  { value: "custom", label: "Personalizado" },
] as const;

type StatusLevel = "green" | "yellow" | "red";

interface HeaderProps {
  onOpenMobileSidebar: () => void;
}

export function Header({ onOpenMobileSidebar }: HeaderProps) {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const { user, signOut } = useAuth();
  const { isDark, toggle } = useDarkMode();
  const { period, startDate, endDate, setPeriod } = usePeriodFilterContext();

  const [menuOpen, setMenuOpen] = useState(false);
  const [systemStatus, setSystemStatus] = useState<StatusLevel>("green");

  const [customFrom, setCustomFrom] = useState(() => (startDate ? format(startDate, "yyyy-MM-dd") : ""));
  const [customTo, setCustomTo] = useState(() => (endDate ? format(endDate, "yyyy-MM-dd") : ""));

  const pageTitle = useMemo(() => getRouteTitle(pathname), [pathname]);

  useEffect(() => {
    async function fetchStatus() {
      const { count, error } = await supabase
        .from("system_errors")
        .select("id", { count: "exact", head: true })
        .eq("resolved", false);

      if (error) {
        setSystemStatus("yellow");
        return;
      }

      if ((count ?? 0) === 0) {
        setSystemStatus("green");
      } else if ((count ?? 0) <= 5) {
        setSystemStatus("yellow");
      } else {
        setSystemStatus("red");
      }
    }

    fetchStatus().catch(() => setSystemStatus("yellow"));
    const interval = window.setInterval(() => {
      fetchStatus().catch(() => setSystemStatus("yellow"));
    }, 60000);

    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    if (period !== "custom") return;

    if (startDate) setCustomFrom(format(startDate, "yyyy-MM-dd"));
    if (endDate) setCustomTo(format(endDate, "yyyy-MM-dd"));
  }, [endDate, period, startDate]);

  const statusClass =
    systemStatus === "green" ? "bg-success" : systemStatus === "yellow" ? "bg-warning" : "bg-destructive";

  return (
    <header className="sticky top-0 z-30 shrink-0 border-b bg-background/95 px-3 py-2 backdrop-blur [@media(max-height:500px)]:px-2 [@media(max-height:500px)]:py-1 sm:px-6 sm:py-3">
      <div className="flex flex-wrap items-center justify-between gap-2 [@media(max-height:500px)]:flex-nowrap [@media(max-height:500px)]:gap-1 sm:gap-4">
        <div className="flex min-w-0 items-center gap-2 [@media(max-height:500px)]:min-w-fit [@media(max-height:500px)]:gap-1 sm:min-w-[200px]">
          <Button
            variant="outline"
            size="icon"
            className="shrink-0 lg:hidden"
            onClick={onOpenMobileSidebar}
            aria-label="Abrir menu lateral"
          >
            <Menu size={16} aria-hidden="true" />
          </Button>
          <div className="min-w-0">
            <h1 className="truncate text-lg font-semibold [@media(max-height:500px)]:sr-only">{pageTitle}</h1>
            <p className="truncate text-xs text-muted-foreground [@media(max-height:500px)]:sr-only">Centro de Comando Admin</p>
          </div>
        </div>

        <div className="flex w-full flex-nowrap items-center gap-2 overflow-x-auto pb-1 [@media(max-height:500px)]:min-w-0 [@media(max-height:500px)]:flex-1 [@media(max-height:500px)]:gap-1 [@media(max-height:500px)]:pb-0 [&>*]:shrink-0 md:w-auto md:flex-wrap md:overflow-visible md:pb-0">
          <label className="sr-only" htmlFor="period-filter">
            Filtro de periodo
          </label>
          <select
            id="period-filter"
            className="h-10 rounded-md border border-input bg-background px-3 text-sm [@media(max-height:500px)]:h-9 [@media(max-height:500px)]:px-2"
            value={period}
            onChange={(event) => setPeriod(event.target.value as typeof period)}
          >
            {periodOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          {period === "custom" && (
            <>
              <input
                type="date"
                value={customFrom}
                onChange={(event) => {
                  const nextFrom = event.target.value;
                  setCustomFrom(nextFrom);
                  if (nextFrom && customTo) {
                    setPeriod("custom", { from: new Date(`${nextFrom}T00:00:00`), to: new Date(`${customTo}T23:59:59`) });
                  }
                }}
                className="h-10 rounded-md border border-input bg-background px-3 text-sm"
              />
              <input
                type="date"
                value={customTo}
                onChange={(event) => {
                  const nextTo = event.target.value;
                  setCustomTo(nextTo);
                  if (customFrom && nextTo) {
                    setPeriod("custom", { from: new Date(`${customFrom}T00:00:00`), to: new Date(`${nextTo}T23:59:59`) });
                  }
                }}
                className="h-10 rounded-md border border-input bg-background px-3 text-sm"
              />
            </>
          )}

          <Button variant="outline" className="gap-2 [@media(max-height:500px)]:h-9 [@media(max-height:500px)]:px-2" onClick={openCommandPalette}>
            <Search size={16} aria-hidden="true" />
            <span className="[@media(max-height:500px)]:sr-only">Buscar</span>
            <Badge variant="secondary" className="gap-1 [@media(max-height:500px)]:hidden">
              <Command size={12} aria-hidden="true" />K
            </Badge>
          </Button>

          <Button variant="outline" size="icon" className="[@media(max-height:500px)]:h-9 [@media(max-height:500px)]:w-9" onClick={toggle} aria-label="Alternar tema escuro">
            {isDark ? <Sun size={16} aria-hidden="true" /> : <Moon size={16} aria-hidden="true" />}
          </Button>

          <div className="flex items-center gap-2 rounded-md border px-3 py-2 text-xs [@media(max-height:500px)]:h-9 [@media(max-height:500px)]:px-2">
            <span className={`h-2.5 w-2.5 rounded-full ${statusClass}`} aria-hidden="true" />
            <span className="[@media(max-height:500px)]:sr-only">Status sistema</span>
          </div>

          <div className="relative">
            <button
              type="button"
              className="flex h-10 items-center gap-2 rounded-md border px-3 text-sm [@media(max-height:500px)]:h-9 [@media(max-height:500px)]:px-1"
              onClick={() => setMenuOpen((current) => !current)}
            >
              <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground">
                {getUserDisplayName(user).slice(0, 1).toUpperCase()}
              </span>
              <span className="hidden sm:block">{getUserDisplayName(user)}</span>
            </button>

            {menuOpen && (
              <div className="absolute right-0 z-50 mt-2 w-64 rounded-md border bg-card p-3 shadow-lg">
                <p className="text-sm font-semibold">{getUserDisplayName(user)}</p>
                <p className="truncate text-xs text-muted-foreground">{user?.email}</p>
                <Button
                  variant="outline"
                  className="mt-3 w-full"
                  onClick={() => {
                    signOut().catch(() => undefined);
                    setMenuOpen(false);
                  }}
                >
                  Sair
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
